import { parseUTC } from '../utils/dateUtils';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { personalNoteApi } from '../api';

const AUTOSAVE_DELAY = 1500; // ms after last keystroke

export default function PersonalNotepad() {
  const [content,    setContent]    = useState('');
  const [savedAt,    setSavedAt]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const autosaveTimer               = useRef(null);
  const isDirty                     = useRef(false);

  // Load on mount
  useEffect(() => {
    personalNoteApi.get()
      .then(r => {
        setContent(r.data.data.content || '');
        if (r.data.data.updatedAt) {
          setSavedAt(parseUTC(r.data.data.updatedAt));
        }
      })
      .catch(() => setError('Failed to load note'))
      .finally(() => setLoading(false));
  }, []);

  const save = useCallback(async (text) => {
    setSaving(true); setError('');
    try {
      await personalNoteApi.save(text);
      setSavedAt(new Date());
      isDirty.current = false;
    } catch {
      setError('Failed to save note');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setContent(val);
    isDirty.current = true;

    // Debounced autosave
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => save(val), AUTOSAVE_DELAY);
  };

  const handleClear = async () => {
    if (!window.confirm('Clear your notepad? This cannot be undone.')) return;
    setContent('');
    isDirty.current = false;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    try {
      await personalNoteApi.clear();
      setSavedAt(null);
    } catch {
      setError('Failed to clear note');
    }
  };

  const formatSavedAt = (d) => {
    if (!d) return '';
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 5)  return 'just saved';
    if (diff < 60) return `saved ${diff}s ago`;
    if (diff < 3600) return `saved ${Math.floor(diff / 60)}m ago`;
    return `saved at ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  if (loading) return (
    <div style={S.container}>
      <div style={S.loadingText}>Loading your notepad…</div>
    </div>
  );

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>My Notepad</h2>
          <p style={S.subtitle}>
            Private to you only — no one else can see this, not even admins.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {saving && (
            <span style={S.savingBadge}>Saving…</span>
          )}
          {!saving && savedAt && (
            <span style={S.savedBadge}>{formatSavedAt(savedAt)}</span>
          )}
          {content && (
            <button onClick={handleClear} style={S.clearBtn}>
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={S.errorBox}>{error}</div>
      )}

      {/* The notepad */}
      <textarea
        value={content}
        onChange={handleChange}
        placeholder={
          "Write anything here — tasks to remember, personal goals, follow-up reminders, " +
          "ideas, or anything you need to keep track of.\n\n" +
          "This is your private space. It autosaves as you type."
        }
        style={S.textarea}
        spellCheck={false}
      />

      <div style={S.footer}>
        <span style={S.charCount}>{content.length} characters</span>
        <span style={S.privacyNote}>🔒 Encrypted and private to you</span>
      </div>
    </div>
  );
}

const S = {
  container: {
    display: 'flex', flexDirection: 'column',
    height: 'calc(100vh - 120px)', minHeight: '400px',
    padding: '20px', gap: '12px',
  },
  header: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
  },
  title:    { fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '2px' },
  subtitle: { fontSize: '13px', color: 'var(--color-text-muted)' },
  savingBadge: {
    fontSize: '12px', color: 'var(--color-text-muted)',
    background: 'var(--color-bg)', padding: '4px 10px',
    borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
  },
  savedBadge: {
    fontSize: '12px', color: 'var(--color-success)',
    background: '#F0FFF4', padding: '4px 10px',
    borderRadius: 'var(--radius-sm)', border: '1px solid #9AE6B4',
  },
  clearBtn: {
    padding: '6px 12px', borderRadius: 'var(--radius-sm)',
    background: 'transparent', color: 'var(--color-danger)',
    border: '1px solid var(--color-danger)',
    cursor: 'pointer', fontSize: '12px',
  },
  errorBox: {
    padding: '10px 14px', background: '#FFF5F5',
    border: '1px solid #FEB2B2', borderRadius: 'var(--radius-sm)',
    fontSize: '13px', color: 'var(--color-danger)',
  },
  textarea: {
    flex: 1, width: '100%',
    padding: '16px', fontSize: '14px',
    lineHeight: 1.7, color: 'var(--color-text)',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    resize: 'none', outline: 'none',
    fontFamily: "'SF Mono', 'Consolas', 'Courier New', monospace",
    transition: 'border-color 0.15s',
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  charCount:   { fontSize: '12px', color: 'var(--color-text-muted)' },
  privacyNote: { fontSize: '12px', color: 'var(--color-text-muted)' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' },
};
